// Centralized Razorpay configuration from Firestore
// This reads the public key ID from Firestore as the single source of truth
// NOTE: Secret key is NEVER exposed to frontend (security risk)

import { getFirestore, doc, getDoc } from 'firebase/firestore';
import { app } from '../firebase/config';

// Cache to avoid repeated Firestore reads
let cachedKeyId = null;
let cacheTimestamp = null;
const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes cache

/**
 * Get Razorpay Key ID from Firestore
 * Falls back to hardcoded value if Firestore read fails
 * @returns {Promise<string>} Razorpay Key ID
 */
export const getRazorpayKeyId = async () => {
  // Return cached value if still valid
  if (cachedKeyId && cacheTimestamp && (Date.now() - cacheTimestamp < CACHE_DURATION)) {
    return cachedKeyId;
  }

  try {
    const db = getFirestore(app);
    const razorpayDoc = await getDoc(doc(db, 'payment_gateway', 'razorpay_live'));
    
    if (razorpayDoc.exists()) {
      const data = razorpayDoc.data();
      const keyId = data?.key || data?.key_id;
      
      if (keyId) {
        // Update cache
        cachedKeyId = keyId;
        cacheTimestamp = Date.now();
        console.log('✅ Razorpay Key ID loaded from Firestore:', keyId);
        return keyId;
      }
    }
    
    console.warn('⚠️ Razorpay config not found in Firestore, using fallback');
  } catch (error) {
    console.error('❌ Error reading Razorpay config from Firestore:', error);
    console.warn('⚠️ Using fallback key ID');
  }

  // Fallback to hardcoded value
  const fallbackKeyId = 'rzp_live_Rc5lG5lbzRgd3a';
  cachedKeyId = fallbackKeyId;
  cacheTimestamp = Date.now();
  return fallbackKeyId;
};

/**
 * Get Razorpay Key ID synchronously (uses cache)
 * Use this when you need immediate access without async
 * @returns {string|null} Cached Razorpay Key ID or null if not cached yet
 */
export const getRazorpayKeyIdSync = () => {
  return cachedKeyId || 'rzp_live_Rc5lG5lbzRgd3a'; // Fallback if cache not ready
};

